import React from "react";
import { Building2, Phone, Mail, IndianRupee, Edit2, Trash2 } from "lucide-react";
import { VendorWithOutstanding } from "@/types/inventory";

type VendorCardProps = {
  vendor: VendorWithOutstanding;
  onEdit?: (vendor: VendorWithOutstanding) => void;
  onDelete?: (vendor: VendorWithOutstanding) => void;
};

export function VendorCard({ vendor, onEdit, onDelete }: VendorCardProps) {
  const hasOutstanding = vendor.outstanding_amount > 0;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900 mb-1">{vendor.name}</h3>
          {vendor.company_name && (
            <p className="text-sm text-gray-500 flex items-center gap-1">
              <Building2 className="w-3 h-3" />
              {vendor.company_name}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {hasOutstanding && (
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
              Outstanding
            </span>
          )}
          {(onEdit || onDelete) && (
            <div className="flex gap-1">
              {onEdit && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(vendor);
                  }}
                  className="p-1.5 text-gray-600 hover:text-[#5B8E87] hover:bg-gray-50 rounded transition-colors"
                  title="Edit vendor"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
              )}
              {onDelete && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(vendor);
                  }}
                  className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                  title="Delete vendor"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="space-y-1 mb-3">
        {vendor.phone && (
          <p className="text-sm text-gray-600 flex items-center gap-2">
            <Phone className="w-3 h-3" />
            {vendor.phone}
          </p>
        )}
        {vendor.email && (
          <p className="text-sm text-gray-600 flex items-center gap-2">
            <Mail className="w-3 h-3" />
            {vendor.email}
          </p>
        )}
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <div>
          <p className="text-xs text-gray-500">Total Purchases</p>
          <p className="text-sm font-medium text-gray-900 flex items-center gap-1">
            <IndianRupee className="w-3 h-3" />
            {Number(vendor.total_purchases || 0).toFixed(2)}
          </p>
        </div>
        {hasOutstanding && (
          <div>
            <p className="text-xs text-gray-500">Outstanding</p>
            <p className="text-sm font-medium text-red-600 flex items-center gap-1">
              <IndianRupee className="w-3 h-3" />
              {Number(vendor.outstanding_amount).toFixed(2)}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
